import { useSession } from '@/api/session';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { t, EXPIRY_KIND_BG } from '@/i18n';
import { daysUntil, formatDate, formatKg, formatPlate } from '@/lib/format';
import { useSaveVehicle, useVehicles } from '@/api/hooks/catalog';
import type { Vehicle } from '@/api/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { Checkbox, Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import {
  CardList,
  CardListEmpty,
  CardListItem,
  DataRow,
  NarrowOnly,
  WideOnly,
} from '@/components/ui/MobileList';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { toast } from '@/stores/toast';

type Draft = Partial<Vehicle> & { id?: string };

const STATUS_BG: Record<string, string> = {
  active: 'В движение',
  in_service: 'В сервиз',
  out_of_service: 'Спрян',
  sold: 'Продаден',
};

export function VehiclesPage() {
  const { data, isLoading, isError, refetch } = useVehicles();
  const save = useSaveVehicle();
  const [draft, setDraft] = useState<Draft | null>(null);
  const isOwner = useSession((st) => st.user?.role) === 'owner';
  const search = useSearch({ from: '/vehicles' });
  const navigate = useNavigate();
  /** Дошли от страницата на буса с „Данни на буса" — формата се отваря сама. */
  useEffect(() => {
    if (!search.edit || !data) return;
    const found = data.items.find((v) => v.id === search.edit);
    if (found) setDraft(found);
    void navigate({ to: '/vehicles', search: {}, replace: true });
  }, [search.edit, data, navigate]);

  const submit = () => {
    if (!draft) return;
    save.mutate(draft, {
      onSuccess: () => {
        toast.ok('Бусът е записан.');
        setDraft(null);
      },
    });
  };

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex items-center gap-3">
        <h1 className="font-display text-lg font-bold tracking-tight">{t.vehicles.title}</h1>
        <span className="text-xs text-ink-dim">{data ? `${data.total} буса` : ''}</span>
        {/* Нов бус/шофьор създава само управителят — сървърът отказва на диспечера. */}
        {isOwner ? (
        <Button
            className="ml-auto"
            variant="primary"
            icon={<Plus className="size-4" />}
            onClick={() => setDraft({ status: 'active' })}
          >
            {t.vehicles.newVehicle}
          </Button>
        ) : null}
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <LoadingBlock />
        ) : isError ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : (
          <>
            {/* Тесен екран: бусът е карта, а сроковете — списък „етикет → стойност". */}
            <NarrowOnly>
              {(data?.items ?? []).length === 0 ? (
                <CardListEmpty>{t.common.empty}</CardListEmpty>
              ) : (
                <CardList>
                  {(data?.items ?? []).map((vehicle) => {
                    const downRated =
                      vehicle.technicalMassKg !== null &&
                      vehicle.permissibleMassKg !== null &&
                      vehicle.technicalMassKg > vehicle.permissibleMassKg;
                    return (
                      <CardListItem key={vehicle.id}>
                        <div className="flex items-baseline gap-2">
                          <Link
                            to="/vehicles/$vehicleId"
                            params={{ vehicleId: vehicle.id }}
                            className="font-display text-base font-semibold hover:text-accent"
                          >
                            {vehicle.fleetNo}
                          </Link>
                          <span className="font-mono text-xs text-ink-faint">
                            {formatPlate(vehicle.plate)}
                          </span>
                          <Badge
                            tone={vehicle.status === 'active' ? 'ok' : 'warn'}
                            className="ml-auto"
                          >
                            {STATUS_BG[vehicle.status] ?? vehicle.status}
                          </Badge>
                        </div>
                        <div className="mt-0.5 text-sm text-ink-dim">
                          {vehicle.make ?? ''} {vehicle.model ?? ''} {vehicle.year ?? ''}{' '}
                          {vehicle.euroStandard ?? ''}
                        </div>

                        <dl className="mt-2">
                          <DataRow label="Товарен отсек">
                            {cargoText(vehicle)}
                          </DataRow>
                          <DataRow label={t.vehicles.massF1}>
                            {formatKg(vehicle.technicalMassKg)}
                            {downRated ? (
                              <span className="ml-1 text-warn" title={t.vehicles.downRated}>
                                (свален до {formatKg(vehicle.permissibleMassKg)})
                              </span>
                            ) : null}
                          </DataRow>
                        </dl>

                        <div className="mt-1.5">
                          <NextExpiry vehicle={vehicle} />
                        </div>

                        <Button
                          className="mt-2 w-full"
                          variant="secondary"
                          onClick={() => setDraft(vehicle)}
                        >
                          {t.common.edit}
                        </Button>
                      </CardListItem>
                    );
                  })}
                </CardList>
              )}
            </NarrowOnly>

            <WideOnly>
              <Table>
                <THead>
                  <TR>
                    <TH>{t.vehicles.fleetNo}</TH>
                    <TH>{t.vehicles.plate}</TH>
                    <TH>{t.vehicles.makeModel}</TH>
                    <TH>{t.common.status}</TH>
                    <TH>Товарен отсек</TH>
                    <TH numeric>Товароносимост</TH>
                    <TH>Следващ срок</TH>
                    <TH>Талон</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {(data?.items ?? []).length === 0 ? (
                    <TableEmpty colSpan={9}>{t.common.empty}</TableEmpty>
                  ) : (
                    (data?.items ?? []).map((vehicle) => {
                      const downRated =
                        vehicle.technicalMassKg !== null &&
                        vehicle.permissibleMassKg !== null &&
                        vehicle.technicalMassKg > vehicle.permissibleMassKg;
                      return (
                        <TR key={vehicle.id}>
                          <TD className="font-display font-semibold">
                            <Link
                              to="/vehicles/$vehicleId"
                              params={{ vehicleId: vehicle.id }}
                              className="hover:text-accent"
                            >
                              {vehicle.fleetNo}
                            </Link>
                          </TD>
                          <TD className="font-mono text-xs">{formatPlate(vehicle.plate)}</TD>
                          <TD className="whitespace-nowrap text-ink-dim">
                            {vehicle.make ?? ''} {vehicle.model ?? ''}
                            <div className="text-[11px] text-ink-faint">
                              {vehicle.year ?? ''} {vehicle.euroStandard ?? ''}
                            </div>
                          </TD>
                          <TD>
                            <Badge tone={vehicle.status === 'active' ? 'ok' : 'warn'}>
                              {STATUS_BG[vehicle.status] ?? vehicle.status}
                            </Badge>
                          </TD>
                          <TD className="whitespace-nowrap text-ink-dim">{cargoText(vehicle)}</TD>
                          <TD numeric>
                            {formatKg(vehicle.payloadKg)}
                            {vehicle.cargoVolumeM3 != null ? (
                              <div className="text-[11px] text-ink-faint">
                                {vehicle.cargoVolumeM3.toLocaleString('bg-BG', { maximumFractionDigits: 1 })} m³
                              </div>
                            ) : null}
                            {downRated ? (
                              <div className="text-[11px] text-warn" title={t.vehicles.downRated}>
                                свален до {formatKg(vehicle.permissibleMassKg)}
                              </div>
                            ) : null}
                          </TD>
                          <TD>
                            <NextExpiry vehicle={vehicle} />
                          </TD>
                          <TD>
                            <Link
                              to="/vehicles/$vehicleId"
                              params={{ vehicleId: vehicle.id }}
                              className={vehicle.registrationDocuments ? 'text-xs text-ok hover:underline' : 'text-xs text-warn hover:underline'}
                              title={vehicle.registrationDocuments ? 'Отвори снимките на талона' : 'Качете снимка на талона'}
                            >
                              {vehicle.registrationDocuments ? `✓ ${vehicle.registrationDocuments} сн.` : 'качи'}
                            </Link>
                          </TD>
                          <TD className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => setDraft(vehicle)}>
                              {t.common.edit}
                            </Button>
                          </TD>
                        </TR>
                      );
                    })
                  )}
                </TBody>
              </Table>
            </WideOnly>
          </>
        )}
      </Card>

      <Dialog
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? t.common.edit : t.vehicles.newVehicle}
        width="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t.common.cancel}
            </Button>
            <Button variant="primary" onClick={submit} loading={save.isPending}>
              {t.common.save}
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t.vehicles.fleetNo} required>
              <Input
                data-autofocus
                value={draft.fleetNo ?? ''}
                onChange={(e) => setDraft({ ...draft, fleetNo: e.target.value })}
              />
            </Field>
            <Field label={t.vehicles.plate} required>
              <Input
                className="font-mono uppercase"
                value={draft.plate ?? ''}
                onChange={(e) => setDraft({ ...draft, plate: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Марка">
              <Input
                value={draft.make ?? ''}
                onChange={(e) => setDraft({ ...draft, make: e.target.value })}
              />
            </Field>
            <Field label="Модел">
              <Input
                value={draft.model ?? ''}
                onChange={(e) => setDraft({ ...draft, model: e.target.value })}
              />
            </Field>
            <Field
              label={t.vehicles.massF1}
              hint="Кл. F.1 от талона — по нея се смята германската пътна такса (Maut)."
            >
              <Input
                type="number"
                className="font-mono tabular"
                value={draft.technicalMassKg ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, technicalMassKg: Number(e.target.value) || null })
                }
              />
            </Field>
            <Field
              label={t.vehicles.massF2}
              hint="Кл. F.2 от талона — допустимата маса: бусът и товарът заедно (при нашите бусове 2 500 кг)."
            >
              <Input
                type="number"
                className="font-mono tabular"
                value={draft.permissibleMassKg ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, permissibleMassKg: Number(e.target.value) || null })
                }
              />
            </Field>
            <Field
              label="Маса в експлоатация (G)"
              hint="Кл. G от талона — бусът с шофьор и гориво. Товароносимостта е F.2 − G."
            >
              <Input
                type="number"
                className="font-mono tabular"
                value={draft.kerbMassKg ?? ''}
                onChange={(e) => setDraft({ ...draft, kerbMassKg: Number(e.target.value) || null })}
              />
            </Field>
            <Field
              label="Товароносимост"
              hint={
                draft.permissibleMassKg && draft.kerbMassKg
                  ? `Смята се от талона: ${draft.permissibleMassKg - draft.kerbMassKg} кг.`
                  : 'Без G от талона — въведете я ръчно.'
              }
            >
              <Input
                type="number"
                className="font-mono tabular"
                disabled={Boolean(draft.permissibleMassKg && draft.kerbMassKg)}
                value={
                  draft.permissibleMassKg && draft.kerbMassKg
                    ? draft.permissibleMassKg - draft.kerbMassKg
                    : (draft.payloadManualKg ?? draft.payloadKg ?? '')
                }
                onChange={(e) => setDraft({ ...draft, payloadManualKg: Number(e.target.value) || null })}
              />
            </Field>
            <Field label="Товарен обем (m³)" hint="Обемът на товарния отсек — по него се пълни бусът.">
              <Input
                type="number"
                step="0.1"
                className="font-mono tabular"
                value={draft.cargoVolumeM3 ?? ''}
                onChange={(e) => setDraft({ ...draft, cargoVolumeM3: Number(e.target.value) || null })}
              />
            </Field>
            <Field label={t.common.status}>
              <Select
                value={draft.status ?? 'active'}
                onChange={(e) =>
                  setDraft({ ...draft, status: e.target.value as Vehicle['status'] })
                }
                options={Object.entries(STATUS_BG).map(([value, label]) => ({ value, label }))}
              />
            </Field>
            <Field label="Норма разход (л/100 км × 100)">
              <Input
                type="number"
                className="font-mono tabular"
                value={draft.fuelNormL100kmX100 ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, fuelNormL100kmX100: Number(e.target.value) || null })
                }
              />
            </Field>
            <div className="flex gap-4 sm:col-span-2">
              <Checkbox
                label="Падащ борд"
                checked={draft.hasTailLift ?? false}
                onChange={(e) => setDraft({ ...draft, hasTailLift: e.target.checked })}
              />
              <Checkbox
                label="ADR оборудване"
                checked={draft.hasAdrEquipment ?? false}
                onChange={(e) => setDraft({ ...draft, hasAdrEquipment: e.target.checked })}
              />
            </div>
            <Field label={t.common.notes} className="sm:col-span-2">
              <Textarea
                rows={2}
                value={draft.notes ?? ''}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </Field>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}

function cargoText(vehicle: Vehicle): string {
  if (!vehicle.cargoLengthCm || !vehicle.cargoWidthCm || !vehicle.cargoHeightCm) return '—';
  return `${vehicle.cargoLengthCm} × ${vehicle.cargoWidthCm} × ${vehicle.cargoHeightCm} см`;
}

/** Само най-близкият срок, с думи: „Технически преглед · след 12 дни". */
function NextExpiry({ vehicle }: { vehicle: Vehicle }) {
  const next = [...(vehicle.expiries ?? [])]
    .filter((e) => e.kind !== 'tachograph_calibration')
    .sort((a, b) => a.validUntil.localeCompare(b.validUntil))[0];
  if (!next) return <span className="text-xs text-ink-faint">няма въведени срокове</span>;
  const days = daysUntil(next.validUntil);
  const tone = days !== null && days < 0 ? 'text-danger' : days !== null && days <= 30 ? 'text-warn' : 'text-ink-dim';
  return (
    <div className="text-xs">
      <div className="font-medium text-ink">{EXPIRY_KIND_BG[next.kind] ?? next.kind}</div>
      <div className={tone}>
        {formatDate(next.validUntil)} ·{' '}
        {days === null ? '' : days < 0 ? `изтекъл преди ${Math.abs(days)} дни` : days === 0 ? 'днес' : `след ${days} дни`}
      </div>
    </div>
  );
}
